import { useState, useCallback, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Popover,
  List,
  ListItemButton,
} from '@mui/material';
import {
  Settings,
  Apps as AppsIcon,
  HelpOutline,
  Logout,
  Business,
  Article,
  Info,
  CloudOutlined,
  ContactSupport,
  DesktopWindows,
  PictureAsPdf,
  Language,
  WarningAmber,
} from '@mui/icons-material';
import { PublishIcon, WebReviewIcon, useAuth } from '@certara/common-react-components';
import { apiClient } from '@/lib/api-client';
import { useUIStore } from '@/stores/ui-store';
import { useCanAccessConfiguration } from '@/hooks/use-configuration';
import { useCompanyStore } from '@/stores/company-store';
import { useCompanies } from '@/hooks/use-companies';
import { useSnackbarStore } from '@certara/common-react-components';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY } from '@certara/common-react-components/i18n';
import { useTranslation } from 'react-i18next';

export function NavBar() {
  const { t, i18n } = useTranslation('load');
  const [helpAnchorEl, setHelpAnchorEl] = useState<null | HTMLElement>(null);
  const [appsAnchorEl, setAppsAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadsInFlight, setDownloadsInFlight] = useState<Set<string>>(new Set());
  // Mirror of downloadsInFlight used for the synchronous double-click guard:
  // state updates are async so a fast second click arrives before React re-renders.
  const downloadsInFlightRef = useRef<Set<string>>(new Set());
  
  const auth = useAuth();
  const { addSnackbar } = useSnackbarStore();
  const setConfigurationDialogOpen = useUIStore(
    (state) => state.setConfigurationDialogOpen
  );
  const setContactDialogOpen = useUIStore(
    (state) => state.setContactDialogOpen
  );
  const setInfoDialogOpen = useUIStore(
    (state) => state.setInfoDialogOpen
  );
  const setErrorsDialogOpen = useUIStore(
    (state) => state.setErrorsDialogOpen
  );

  const { activeCompany, setActiveCompany } = useCompanyStore();
  const { data: companies = [], error: companiesError, isLoading: companiesLoading } = useCompanies();
  const { data: canAccessConfiguration, isLoading: isPermLoading } = useCanAccessConfiguration(
    activeCompany?.Id
  );

  // Configuration button visibility logic:
  // - Show if user explicitly has permission
  // - Show optimistically while loading (unless explicitly denied)
  // - Hide only if explicitly denied
  const hasExplicitPermission = canAccessConfiguration === true;
  const isLoadingWithNoExplicitDenial = isPermLoading && canAccessConfiguration !== false;
  const showConfigButton = activeCompany && (hasExplicitPermission || isLoadingWithNoExplicitDenial);

  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpMenuClose = useCallback(() => {
    setHelpAnchorEl(null);
  }, []);

  const handleAppsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAppsAnchorEl(event.currentTarget);
  };

  const handleAppsMenuClose = () => {
    setAppsAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await auth.logout();
    } catch (error) {
      addSnackbar(
        t('nav.snackbarSignOutError'),
        'error',
        error instanceof Error ? error.message : String(error),
        true
      );
    }
  };

  const handleCompanyChange = (companyId: number) => {
    const company = companies.find(c => c.Id === companyId);
    if (company) {
      setActiveCompany(company);
    }
  };

  const handleConfigurationClick = () => {
    setConfigurationDialogOpen(true);
  };

  const openUserGuide = useCallback(async () => {
    const fallbackUrl = 'https://onlinehelp-dev.certara.com/globalsubmit/25.9.1/index.html#t=GlobalSubmit%2FLoad%2FLoad.htm';
    try {
      const url = await apiClient.get<string>('api/Main/GetUserGuideUrl');
      window.open(url || fallbackUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // Fall back to hardcoded URL if API fails
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
    handleHelpMenuClose();
  }, []);

  const downloadFile = useCallback(async (endpoint: string, filename: string) => {
    // Atomic guard against fast double-clicks: setState is async so a second
    // click can arrive before the MenuItem re-renders as disabled.
    if (downloadsInFlightRef.current.has(endpoint)) return;
    downloadsInFlightRef.current.add(endpoint);
    setDownloadsInFlight(new Set(downloadsInFlightRef.current));
    addSnackbar(t('nav.snackbarPreparingDownload', { filename }), 'info');
    try {
      // apiClient.getBlob attaches the bearer and handles 401 → refresh → retry
      // → visible sign-in via authedFetch; base URL is prepended for us.
      const blob = await apiClient.getBlob(`/api/Main/${endpoint}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      addSnackbar(
        t('nav.snackbarDownloadFailed', { filename }),
        'error',
        error instanceof Error ? error.message : String(error),
        true
      );
    } finally {
      downloadsInFlightRef.current.delete(endpoint);
      setDownloadsInFlight(new Set(downloadsInFlightRef.current));
      handleHelpMenuClose();
    }
  }, [addSnackbar, t, handleHelpMenuClose]);

  return (
    <AppBar
      data-testid="navbar"
      position="fixed"
      elevation={2}
      sx={{
        bgcolor: 'white',
        color: 'text.primary',
        height: 64,
      }}
    >
      <Toolbar sx={{ minHeight: 64, height: 64 }}>
        <CloudOutlined sx={{ mr: 2, color: 'text.primary' }} />
        <Typography variant="h6" noWrap component="div" sx={{ ml: 2, mr: 3, color: 'text.primary' }} data-testid="navbar-app-title">
          {t('nav.appTitle')}
        </Typography>

        {/* Company Selector */}
        {companiesError ? (
          <FormControl size="small" sx={{ minWidth: 250, mr: 2 }} data-testid="navbar-company-selector-error">
            <Select
              value=""
              disabled
              sx={{ 
                bgcolor: 'error.light', 
                color: 'error.contrastText',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'error.main',
                },
              }}
              displayEmpty
              data-testid="navbar-company-select-error"
            >
              <MenuItem value="">
                <Box display="flex" alignItems="center" gap={1}>
                  <Business fontSize="small" />
                  {t('nav.spacesUnavailable')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        ) : companiesLoading ? (
          <FormControl size="small" sx={{ minWidth: 250, mr: 2 }} data-testid="navbar-company-selector-loading">
            <Select
              value=""
              disabled
              sx={{ bgcolor: 'grey.100' }}
              displayEmpty
              data-testid="navbar-company-select-loading"
            >
              <MenuItem value="">
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} data-testid="navbar-company-loading-spinner" />
                  {t('nav.loadingSpaces')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        ) : companies.length > 0 ? (
          <FormControl size="small" sx={{ minWidth: 250, mr: 2 }} data-testid="navbar-company-selector">
            <InputLabel>{t('nav.selectSpace')}</InputLabel>
            <Select
              data-testid="navbar-company-select"
              value={activeCompany?.Id || ''}
              label={t('nav.selectSpace')}
              onChange={(e) => handleCompanyChange(e.target.value as number)}
              sx={{ bgcolor: 'white' }}
            >
              {companies.map((company) => (
                <MenuItem key={company.Id} value={company.Id} data-testid={`navbar-company-option-${company.Id}`}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Business fontSize="small" />
                    {company.Name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <Box sx={{ flexGrow: 1 }} />

        {/* Configuration Button (if user has access) */}
        {showConfigButton && (
          <Tooltip title={t('nav.tooltipConfiguration')}>
            <span>
              <IconButton
                data-testid="navbar-configuration-button"
                color="inherit"
                onClick={handleConfigurationClick}
                disabled={!activeCompany}
                sx={{ mr: 1 }}
              >
                <Settings />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Cloud Applications (Nine-dot Apps Menu) */}
        <Tooltip title={t('nav.tooltipCloudApplications')}>
          <IconButton
            data-testid="navbar-apps-menu-button"
            color="inherit"
            onClick={handleAppsMenuOpen}
            sx={{ mr: 1 }}
          >
            <AppsIcon />
          </IconButton>
        </Tooltip>

        <Menu
          data-testid="navbar-apps-menu"
          anchorEl={appsAnchorEl}
          open={Boolean(appsAnchorEl)}
          onClose={handleAppsMenuClose}
          sx={{ 
            mt: 1,
            '& .MuiPaper-root': {
              minWidth: 140,
              width: 140,
              padding: '10px 15px 10px 5px',
            }
          }}
        >
          {/* Cloud Applications - equivalent to Vue NineDot component */}
          <MenuItem
            data-testid="navbar-apps-menu-publish"
            component="a"
            href={`${window.location.origin}/publish`}
            target="_blank"
            onClick={handleAppsMenuClose}
            sx={{ minHeight: 35 }}
          >
            <ListItemIcon>
              <PublishIcon size={20} />
            </ListItemIcon>
            <ListItemText>{t('nav.appsPublish')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-apps-menu-review"
            component="a"
            href={`${window.location.origin}/webreview`}
            target="_blank"
            onClick={handleAppsMenuClose}
            sx={{ minHeight: 35 }}
          >
            <ListItemIcon>
              <WebReviewIcon size={20} />
            </ListItemIcon>
            <ListItemText>{t('nav.appsReview')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-apps-menu-security"
            component="a"
            href={`${window.location.origin}/security`}
            target="_blank"
            onClick={handleAppsMenuClose}
            sx={{ minHeight: 35 }}
          >
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('nav.appsSecurity')}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Help Menu */}
        <Tooltip title={t('nav.tooltipHelp')}>
          <IconButton
            data-testid="navbar-help-button"
            color="inherit"
            onClick={handleHelpMenuOpen}
            sx={{ mr: 1 }}
          >
            <HelpOutline />
          </IconButton>
        </Tooltip>

        <Menu
          data-testid="navbar-help-menu"
          anchorEl={helpAnchorEl}
          open={Boolean(helpAnchorEl)}
          onClose={handleHelpMenuClose}
          sx={{ mt: 1 }}
        >
          <MenuItem
            data-testid="navbar-help-menu-contact"
            onClick={() => {
              setContactDialogOpen(true);
              handleHelpMenuClose();
            }}
          >
            <ListItemIcon>
              <ContactSupport fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('nav.helpContactUs')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-help-menu-about"
            onClick={() => {
              setInfoDialogOpen(true);
              handleHelpMenuClose();
            }}
          >
            <ListItemIcon>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('nav.helpAbout')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-help-menu-userguide"
            onClick={() => {
              openUserGuide();
              handleHelpMenuClose();
            }}
          >
            <ListItemIcon>
              <Article fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('nav.helpUserDoc')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-help-menu-errors"
            onClick={() => {
              setErrorsDialogOpen(true);
              handleHelpMenuClose();
            }}
          >
            <ListItemIcon>
              <WarningAmber fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('nav.helpErrors')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-help-menu-download-desktop"
            disabled={downloadsInFlight.has('DownloadDesktop')}
            onClick={() => downloadFile('DownloadDesktop', 'setup-review.exe')}
          >
            <ListItemIcon>
              {downloadsInFlight.has('DownloadDesktop')
                ? <CircularProgress size={18} />
                : <DesktopWindows fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{t('nav.helpDownloadDesktop')}</ListItemText>
          </MenuItem>

          <MenuItem
            data-testid="navbar-help-menu-download-plugin"
            disabled={downloadsInFlight.has('DownloadAcrobatPlugin')}
            onClick={() => downloadFile('DownloadAcrobatPlugin', 'GSAdobePlugin.msi')}
          >
            <ListItemIcon>
              {downloadsInFlight.has('DownloadAcrobatPlugin')
                ? <CircularProgress size={18} />
                : <PictureAsPdf fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{t('nav.helpDownloadAcrobatPlugin')}</ListItemText>
          </MenuItem>

        </Menu>

        {/* Language Selector */}
        <Tooltip title={t('configuration.language')}>
          <IconButton
            color="inherit"
            onClick={(e) => setLangAnchorEl(e.currentTarget)}
            sx={{ mr: 0.5 }}
          >
            <Language />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(langAnchorEl)}
          anchorEl={langAnchorEl}
          onClose={() => setLangAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <List dense sx={{ minWidth: 160 }}>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <ListItemButton
                key={lang.code}
                selected={i18n.language === lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.code);
                  setLangAnchorEl(null);
                }}
              >
                {lang.nativeLabel}
              </ListItemButton>
            ))}
          </List>
        </Popover>

        {/* Sign Out Button */}
        <Tooltip title={t('nav.tooltipSignOut')}>
          <IconButton
            data-testid="navbar-signout-button"
            color="inherit"
            onClick={handleSignOut}
            sx={{ mr: 1 }}
          >
            <Logout />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}